import React, { useMemo } from 'react';
import { bindPopover } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';

import { Offer_Engagement_Type, OfferFilter } from '@occc/graphql';
import { useGetMembershipOffers } from '@occc-app/dataAccessLayer/membership/queries';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import {
  Eligibility,
  EligibilityType,
  OffersFilterFormProps,
  OffersFilterFormValues,
} from './types';
import { OffersFilterForm } from './OffersFilterForm';
import { headers, makeFields, sourceFilterOptionsMap } from './utils';
import { useEligibility, useEngagementType, useVisibleStatus } from './hooks';

import OffersTable from '../components/OffersTable';
import { OFFERS_AFTER_PARAM, OFFERS_BEFORE_PARAM } from '../../../constants';

interface OffersContainerProps {
  memberId: string;
  searchText?: string;
  popoverProps: ReturnType<typeof bindPopover>;
}

const allEngagementTypes: Offer_Engagement_Type[] = [
  Offer_Engagement_Type.Promotional,
  Offer_Engagement_Type.Purchaseable,
  Offer_Engagement_Type.Transactional,
  Offer_Engagement_Type.PunchcardChallenge,
  Offer_Engagement_Type.Vouchered,
  Offer_Engagement_Type.SweepstakesEntry,
  Offer_Engagement_Type.SweepstakesPurchasable,
  Offer_Engagement_Type.SweepstakesTransactional,
  Offer_Engagement_Type.Behavioral,
  Offer_Engagement_Type.Base,
  Offer_Engagement_Type.ExternallyFulfillable,
  Offer_Engagement_Type.Promocode,
  Offer_Engagement_Type.Game,
  Offer_Engagement_Type.Billboard,
  Offer_Engagement_Type.StoredValue,
];

export const OffersContainer: React.FC<OffersContainerProps> = ({
  memberId,
  searchText,
  popoverProps,
}) => {
  const [engagementType, setEngagementType] = useEngagementType();
  const [eligibility, setEligibility] = useEligibility();
  const [visibleStatus, setVisibleStatus] = useVisibleStatus();

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: OFFERS_BEFORE_PARAM,
    afterParamName: OFFERS_AFTER_PARAM,
  });

  const filter: OfferFilter = useMemo(() => {
    const isEligible = (eligibilityType: Eligibility) => {
      if (eligibilityType !== 'any') {
        return {
          isEligible: eligibility === EligibilityType.Eligible,
        };
      }

      return {};
    };

    return {
      engagementTypeIncludes:
        engagementType !== 'any' ? sourceFilterOptionsMap[engagementType] : allEngagementTypes,
      visibleStatusEquals: visibleStatus !== 'any' ? visibleStatus : undefined,
      search: searchText ?? null,
      deleted: false,
      ...isEligible(eligibility),
    };
  }, [engagementType, searchText, visibleStatus, eligibility]);

  const { data, loading, error, paginationProps } = useGetMembershipOffers({
    membershipId: memberId,
    filter,
    paginationInfo,
    onPageChange,
  });

  const onSubmit: OffersFilterFormProps['onSubmit'] = ({
    engagementType,
    eligibility,
    visibleStatus,
  }) => {
    setEngagementType(engagementType);
    setEligibility(eligibility);
    setVisibleStatus(visibleStatus);
    popoverProps.onClose();
  };

  const onReset = () => {
    setEngagementType('any');
    setEligibility('any');
    setVisibleStatus('any');
    popoverProps.onClose();
  };

  const filterInitialValues: OffersFilterFormValues = useMemo(() => {
    return {
      engagementType,
      eligibility,
      visibleStatus,
    };
  }, [engagementType, eligibility, visibleStatus]);

  return (
    <>
      <OffersTable
        offers={data}
        error={error}
        loading={loading}
        paginationProps={paginationProps}
        headers={headers}
        makeFields={makeFields}
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
      >
        <OffersFilterForm
          onSubmit={onSubmit}
          onReset={onReset}
          initialValues={filterInitialValues}
        />
      </Popover>
    </>
  );
};
